import React from 'react';
import { FamilyGroup } from '../../types';

interface Props {
  families: FamilyGroup[];
  totalPax: number;
  totalChildren: number;
  totalChildrenAges: number[];
  onChange: (families: FamilyGroup[]) => void;
}

const FamilySplitConfig: React.FC<Props> = ({ families, totalPax, totalChildren, totalChildrenAges, onChange }) => {
  const addFamily = () => {
    const newId = families.length > 0 ? Math.max(...families.map(f => f.family_id)) + 1 : 1;
    onChange([...families, { family_id: newId, no_of_pax: 1, no_of_kids: 0, kids_ages: [] }]);
  };

  const removeFamily = (index: number) => {
    onChange(families.filter((_, i) => i !== index));
  };

  const updateFamily = (index: number, updates: Partial<FamilyGroup>) => {
    const updated = [...families];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const updateKidAge = (familyIndex: number, kidIndex: number, age: number) => {
    const updated = [...families];
    const newAges = [...updated[familyIndex].kids_ages];
    newAges[kidIndex] = age;
    updated[familyIndex].kids_ages = newAges;
    onChange(updated);
  };

  const addKid = (familyIndex: number) => {
    const updated = [...families];
    updated[familyIndex].kids_ages.push(0);
    updated[familyIndex].no_of_kids = updated[familyIndex].kids_ages.length;
    onChange(updated);
  };

  const removeKid = (familyIndex: number, kidIndex: number) => {
    const updated = [...families];
    updated[familyIndex].kids_ages.splice(kidIndex, 1);
    updated[familyIndex].no_of_kids = updated[familyIndex].kids_ages.length;
    onChange(updated);
  };

  const totalFamilyPax = families.reduce((sum, f) => sum + f.no_of_pax, 0);
  const totalFamilyKids = families.reduce((sum, f) => sum + f.no_of_kids, 0);
  const paxMatch = totalFamilyPax === totalPax;
  const kidsMatch = totalFamilyKids === totalChildren;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-gray-700">Family Groups (for accommodation splitting)</label>
        <button
          type="button"
          onClick={addFamily}
          className="text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
        >
          + Add Family
        </button>
      </div>

      {families.map((family, index) => (
        <div key={family.family_id} className="border border-gray-300 rounded-lg p-3 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-700">Family {family.family_id}</span>
            {families.length > 1 && (
              <button
                type="button"
                onClick={() => removeFamily(index)}
                className="text-xs text-red-600 hover:text-red-800"
              >
                ✕ Remove
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Adults</label>
              <input
                type="number"
                min="1"
                value={family.no_of_pax}
                onChange={(e) => updateFamily(index, { no_of_pax: parseInt(e.target.value) || 1 })}
                className="input-field text-sm w-full"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Children</label>
              <div className="flex gap-1">
                <input
                  type="number"
                  min="0"
                  value={family.no_of_kids}
                  readOnly
                  className="input-field text-sm w-full bg-gray-100"
                />
                <button
                  type="button"
                  onClick={() => addKid(index)}
                  className="text-xs bg-blue-500 text-white px-2 rounded hover:bg-blue-600"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {family.kids_ages.length > 0 && (
            <div className="mt-2">
              <label className="block text-xs text-gray-600 mb-1">Children Ages</label>
              <div className="flex flex-wrap gap-2">
                {family.kids_ages.map((age, kidIndex) => (
                  <div key={kidIndex} className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      max="17"
                      value={age}
                      onChange={(e) => updateKidAge(index, kidIndex, parseInt(e.target.value) || 0)}
                      className="input-field text-xs w-16"
                      placeholder="Age"
                    />
                    <button
                      type="button"
                      onClick={() => removeKid(index, kidIndex)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {families.length > 0 && (
        <div className={`text-xs p-2 rounded ${paxMatch && kidsMatch ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
          <strong>Validation:</strong> Total: {totalFamilyPax} adults, {totalFamilyKids} kids
          {!paxMatch && <span className="ml-2">⚠️ Adults don't match ({totalPax} expected)</span>}
          {!kidsMatch && <span className="ml-2">⚠️ Kids don't match ({totalChildren} expected)</span>}
          {paxMatch && kidsMatch && <span className="ml-2">✓ Matches totals</span>}
        </div>
      )}
    </div>
  );
};

export default FamilySplitConfig;
